import type { SelectOption } from '@leafwell/components';

import type { SelectList, SelectFieldControlledProps } from './select-field';

import React, { useCallback, useMemo, useState } from 'react';
import { useToast } from '@leafwell/components';
import { useWordPressContext } from 'contexts/wordpress-provider';
import { useDebounce } from 'hooks/useDebounce';

import { SelectFieldControlled, MAX_BADGES_PLACEHOLDER } from './select-field';

export type SelectMultipleVariables = {
  hasNextPage: boolean;
  limit: number;
  offset: number | string;
  search?: string;
};

export type SelectMultipleGetOptionsAttrs = Pick<
  SelectMultipleVariables,
  'offset' | 'search'
>;

export type SelectMultipleGetOptions = (
  variables: SelectMultipleGetOptionsAttrs,
) => Promise<{
  data: unknown[];
  variables: Partial<SelectMultipleVariables>;
}>;

export type SelectMultipleProps = Pick<
  SelectFieldControlledProps<any>,
  'label' | 'name' | 'placeholder'
> & {
  initialData?: SelectFieldControlledProps<any>['options'];
  dataSettings: {
    cursorPagination?: {
      hasNextPage: boolean;
      endCursor: string;
    };
    groupKey?: string;
    key: string;
    labelKey: string;
    getOptions?: SelectMultipleGetOptions;
  };
  limit?: number;
};

const SelectMultiple: React.FC<SelectMultipleProps> = ({
  label,
  name,
  initialData = [],
  limit = 999,
  placeholder,
  dataSettings: { cursorPagination, ...dataSettings },
}) => {
  const { addToast } = useToast();
  const { translations } = useWordPressContext();
  const [variables, updateVariables] = useState<SelectMultipleVariables>({
    hasNextPage: cursorPagination?.hasNextPage || true,
    limit,
    offset: cursorPagination ? cursorPagination.endCursor : 0,
  });

  const getNextOffset = useCallback(
    (isScrollToBottom: boolean) => {
      if (isScrollToBottom) {
        return cursorPagination
          ? variables.offset
          : (variables.offset as number) + variables.limit;
      }

      return cursorPagination ? '' : 0;
    },
    [cursorPagination, variables.limit, variables.offset],
  );

  const mapOptions = useCallback(
    (data: unknown[]): SelectList => {
      let options: SelectList = [];

      if (dataSettings?.groupKey) {
        // @ts-ignore
        options = data.reduce((list: SelectList, item) => {
          const groupLabel = item[dataSettings.groupKey];
          const group = list.find(
            el => el.label === item[dataSettings.groupKey],
          );
          const option = {
            label: item[dataSettings.labelKey],
            value: item[dataSettings.key],
          };

          if (group) {
            // @ts-ignore
            group.options.push(option);
          } else {
            list.push({
              label: groupLabel,
              options: [option],
            });
          }

          return list;
        }, []);
      } else {
        options = data.map(item => ({
          label: item[dataSettings.labelKey],
          value: item[dataSettings.key],
        }));
      }

      return options;
    },
    [dataSettings],
  );

  const updateOptions = useCallback(
    async ({
      offset,
      search,
    }: Pick<SelectMultipleVariables, 'offset' | 'search'>): Promise<
      SelectOption[]
    > => {
      try {
        const { data, variables } = await dataSettings?.getOptions({
          offset,
          search,
        });

        if (data?.length) {
          updateVariables(prev => ({
            ...prev,
            ...variables,
          }));
          // @ts-ignore
          return mapOptions(data);
        }

        return [];
      } catch (error) {
        addToast({
          content: error?.message,
          key: 'multi-select-error',
          type: 'negative',
          shouldRemove: true,
        });
      }
    },
    [mapOptions, dataSettings?.getOptions, updateVariables],
  );

  const debounceUpdateOptions = useDebounce<
    SelectMultipleGetOptionsAttrs,
    Promise<SelectOption[]>
  >(
    async ({ offset, search }) =>
      await updateOptions({
        offset,
        search,
      }),
    300,
  );

  return (
    <SelectFieldControlled
      {...{
        hideSelectedOptions: false,
        label,
        labelSize: 'xsmall',
        maxBadges: 0,
        maxBadgesMessage: translations['%d options selected'].replace(
          '%d',
          MAX_BADGES_PLACEHOLDER,
        ),
        multiple: true,
        name,
        options: useMemo(() => mapOptions(initialData), [initialData]),
        placeholder,
        ...(dataSettings?.getOptions
          ? {
              async: true,
              loadOnRender: false,
              loadOptions: async (value, isScrollToBottom) => {
                if (isScrollToBottom && !variables.hasNextPage) {
                  return [];
                }

                const params = {
                  offset: getNextOffset(isScrollToBottom),
                  search: value,
                };

                // debounce search queries
                if (value && !isScrollToBottom) {
                  return await debounceUpdateOptions(params);
                }

                return await updateOptions(params);
              },
            }
          : {}),
      }}
    />
  );
};

export default SelectMultiple;
